import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { StipendRequestService } from '@app/v2/services/stipend-request/stipend-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Chip, MdsChipComponent, MdsDividerComponent } from '@medpacesoftwaredevelopment/designsystem';
import { DateFormat } from '@models/date-format';
import { PaymentRequestStatus, StipendRequest } from '@models/request';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { RequestMerchantStateService } from '@services/state-management/request-merchant-state.service';
import { combineLatest, map } from 'rxjs';
import { PaymentRequestDeductionViewComponent } from '../../payment-request-deduction/payment-request-deduction-view/payment-request-deduction-view.component';
import {
    computeAuthorizedAmountPatientsCurrencyChip,
    computeAuthorizedAmountUSDChip,
} from '../../reimbursement-request/expense/expense-chips.util';

@Component({
    selector: 'medpace-stipend-request-view',
    imports: [
        AsyncPipe,
        dateFormatPipe,
        UpperCasePipe,
        DecimalPipe,
        PaymentRequestDeductionViewComponent,
        MdsDividerComponent,
        MdsChipComponent,
    ],
    templateUrl: './stipend-request-view.component.html',
    styleUrl: './stipend-request-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StipendRequestViewComponent {
    DateFormat = DateFormat;
    request = input.required<StipendRequest>();
    request$ = toObservable(this.request);
    requestMerchantStateService = inject(RequestMerchantStateService);
    requestService = inject(StipendRequestService);
    userService = inject(UserServiceV2);
    merchantTypes$ = this.requestMerchantStateService.getPaymentRequestMerchants();
    merchant$ = combineLatest({
        request: this.request$,
        merchantTypes: this.merchantTypes$,
    }).pipe(
        map(({ merchantTypes, request }) => {
            return merchantTypes?.find((merchant) => merchant.value === request.merchant);
        })
    );

    elevatedAccess$ = this.userService.permissions.request.access.elevated$;
    isInProcessOrHigher = toSignal(this.requestService.isInProcessOrHigher$);

    private readonly patient = toSignal(inject(PatientStateService).getPatient());
    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    authorizedAmountPatientsCurrencyChip = computed((): Chip[] | null =>
        computeAuthorizedAmountPatientsCurrencyChip(this.request(), this.patientPreferredCurrency())
    );

    authorizedAmountUSDChip = computed((): Chip[] | null => computeAuthorizedAmountUSDChip(this.request()));

    isVoided$ = this.request$.pipe(
        map((request) => {
            if (!request) return null;
            return request.status?.statusId === PaymentRequestStatus.Voided;
        })
    );
}
